import { ModalHeader } from '@unistocks/components/modal/modal-header';
import { Sheet, SheetProps } from '@unistocks/components/sheet/sheet';
import { breakpoints } from '@unistocks/shared/breakpoints';
import { useBackHandler } from '@unistocks/shared/hooks/use-backhandler';
import React, { useCallback } from 'react';
import { Adapt, AdaptPlatform, AdaptWhen, Dialog, DialogProps, ThemeableStackProps } from 'tamagui';
import { useModalContext } from '../../providers/modal-context';

export type ModalProps = DialogProps & {
  adaptWhen?: AdaptWhen;
  adaptPlatform?: AdaptPlatform;
} & {
  /**
   * The title of the modal.
   */
  headerTitle?: string;
  headerSubtitle?: string;
  headerIcon?: React.ReactNode;
  /**
   * Whether to wrap the content in a ScrollView when adapting to sheet.
   * @default true
   */
  wrapInScrollView?: boolean;
  /**
   * Props to pass to the sheet when adapting to sheet.
   */
  sheetProps?: Omit<SheetProps, 'open' | 'onOpenChange'>;
  contentContainerProps?: ThemeableStackProps;
};

export const Modal: React.FC<ModalProps> = ({
  children,
  headerTitle,
  headerSubtitle,
  headerIcon,
  open,
  onOpenChange,
  wrapInScrollView = true,
  sheetProps,
  adaptWhen = 'max-sm' as AdaptWhen,
  adaptPlatform = 'native',
  contentContainerProps,
  ...props
}) => {
  useModalContext(open ?? false);

  useBackHandler(
    useCallback(() => {
      if (open) {
        onOpenChange?.(false);

        return true;
      }

      return false;
    }, [onOpenChange, open])
  );

  return (
    <Dialog modal open={open} onOpenChange={onOpenChange} {...props}>
      <Dialog.Portal $platform-web={{ justifyContent: 'flex-start' }}>
        <Dialog.Overlay
          transition={'500ms'}
          animateOnly={['opacity']}
          bg={'$shadow6'}
          enterStyle={{ o: 0 }}
          exitStyle={{ o: 0 }}
          onPress={() => onOpenChange?.(false)}
        />

        <Dialog.Content
          bordered
          elevate
          transition={'quickLessBouncy'}
          animateOnly={['transform', 'opacity']}
          borderRadius={'$6'}
          p={'$3'}
          mx={'$3'}
          w={'95%'}
          maxW={'95%'}
          enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
          exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
          $sm={{ maxWidth: breakpoints.xs, mx: 'auto' }}
          $md={{ maxWidth: breakpoints.sm, mx: 'auto' }}
          $platform-web={{ mt: '$8' }}
          {...contentContainerProps}
        >
          <ModalHeader title={headerTitle} subtitle={headerSubtitle} icon={headerIcon} />
          {children}
        </Dialog.Content>
      </Dialog.Portal>

      <Adapt when={adaptWhen} platform={adaptPlatform}>
        <Sheet
          headerTitle={headerTitle}
          headerSubtitle={headerSubtitle}
          headerIcon={headerIcon}
          {...sheetProps}
        >
          <Adapt.Contents />
        </Sheet>
      </Adapt>
    </Dialog>
  );
};

Modal.displayName = 'Modal';
